import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Pencil, UserRoundX, UserRoundCheck, Eye, Mail, FileText, Check, X, Clock3, Unlock, Lock } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import AppLayout from "@/components/AppLayout";
import DeleteAllStudentsCard from "@/components/DeleteAllStudentsCard";

function StudentForm({ initial, onSubmit, onCancel, submitLabel }) {
  const [name, setName] = useState(initial?.name || "");
  const [email, setEmail] = useState(initial?.email || "");
  const [password, setPassword] = useState("");
  const [level, setLevel] = useState(initial?.current_level ?? 1);
  const [optIn, setOptIn] = useState(initial?.leaderboard_opt_in ?? true);
  const [parentEmail, setParentEmail] = useState(initial?.parent_email || "");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await onSubmit({ name, email, password, current_level: Number(level), leaderboard_opt_in: optIn, parent_email: parentEmail });
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-4 p-6 bg-white rounded-3xl card-tactile" data-testid="student-form">
      <h3 className="font-heading text-xl font-bold text-ink">{submitLabel}</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-bold text-ink mb-1">Name</label>
          <input data-testid="student-name-input" required value={name} onChange={(e) => setName(e.target.value)} className="w-full h-12 px-4 rounded-2xl border-2 border-cloud focus:border-sky focus:outline-none" />
        </div>
        <div>
          <label className="block text-sm font-bold text-ink mb-1">Email</label>
          <input data-testid="student-email-input" required type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full h-12 px-4 rounded-2xl border-2 border-cloud focus:border-sky focus:outline-none" />
        </div>
        <div>
          <label className="block text-sm font-bold text-ink mb-1">
            {initial ? "New password (leave blank to keep current)" : "Password"}
          </label>
          <input data-testid="student-password-input" type="text" required={!initial} value={password} onChange={(e) => setPassword(e.target.value)} className="w-full h-12 px-4 rounded-2xl border-2 border-cloud focus:border-sky focus:outline-none" />
        </div>
        <div>
          <label className="block text-sm font-bold text-ink mb-1">Current level</label>
          <input data-testid="student-level-input" type="number" min="1" value={level} onChange={(e) => setLevel(e.target.value)} className="w-full h-12 px-4 rounded-2xl border-2 border-cloud focus:border-sky focus:outline-none" />
        </div>
        <div className="sm:col-span-2">
          <label className="block text-sm font-bold text-ink mb-1">Parent email (optional)</label>
          <input data-testid="student-parent-email-input" type="email" value={parentEmail} onChange={(e) => setParentEmail(e.target.value)} placeholder="parent@example.com" className="w-full h-12 px-4 rounded-2xl border-2 border-cloud focus:border-sky focus:outline-none" />
          <p className="text-xs text-stone mt-1">If set, monthly progress reports will also be delivered here.</p>
        </div>
        <label className="sm:col-span-2 inline-flex items-center gap-3 p-3 rounded-2xl bg-canvas cursor-pointer">
          <input
            data-testid="student-optin-checkbox"
            type="checkbox"
            checked={optIn}
            onChange={(e) => setOptIn(e.target.checked)}
            className="w-5 h-5 accent-watermelon"
          />
          <span className="font-semibold text-ink text-sm">Show on weekly leaderboard</span>
        </label>
      </div>
      <div className="flex gap-3 justify-end">
        <button type="button" onClick={onCancel} className="h-12 px-5 rounded-full bg-white border-2 border-cloud text-ink font-bold btn-tactile">Cancel</button>
        <button data-testid="student-form-submit" type="submit" disabled={busy} className="h-12 px-6 rounded-full bg-watermelon text-white font-bold btn-tactile hover:bg-watermelon-hover disabled:opacity-70">
          {busy ? "Saving…" : submitLabel}
        </button>
      </div>
    </form>
  );
}

export default function AdminStudents() {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null);
  const [detail, setDetail] = useState(null);
  const [sending, setSending] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/students");
      setStudents(data || []);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const create = async (payload) => {
    try {
      if (!payload.password) { toast.error("Password required"); return; }
      await api.post("/admin/students", payload);
      toast.success("Student created");
      setShowAdd(false);
      await load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };

  const update = async (id, payload) => {
    try {
      if (!payload.password) delete payload.password;
      await api.put(`/admin/students/${id}`, payload);
      toast.success("Student updated");
      setEditing(null);
      await load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };

  const toggleActive = async (s) => {
    try {
      await api.put(`/admin/students/${s.id}`, { is_active: !s.is_active });
      toast.success(!s.is_active ? "Student activated" : "Student deactivated");
      await load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };

  const approvePending = async (s) => {
    const lvlStr = window.prompt(`Approve ${s.name}? Set starting level (1-10):`, "1");
    if (lvlStr === null) return;
    const lvl = parseInt(lvlStr, 10);
    if (isNaN(lvl) || lvl < 1) { toast.error("Invalid level"); return; }
    try {
      await api.post(`/admin/students/${s.id}/approve`, { current_level: lvl });
      toast.success(`${s.name} approved at Level ${lvl}`);
      await load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };

  const rejectPending = async (s) => {
    if (!window.confirm(`Reject ${s.name}'s signup? They won't be able to log in.`)) return;
    try {
      await api.post(`/admin/students/${s.id}/reject`);
      toast.success(`${s.name} rejected`);
      await load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };

  const setAccess = async (s, grant) => {
    const msg = grant ? `Give ${s.name} free access without payment?` : `Revoke ${s.name}'s free access? They'll need to pay to continue.`;
    if (!window.confirm(msg)) return;
    try {
      await api.post(`/admin/students/${s.id}/${grant ? "grant" : "revoke"}-access`);
      toast.success(grant ? `Free access granted to ${s.name}` : `Free access revoked for ${s.name}`);
      await load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };

  const accessBadge = (s) => {
    if (!s.is_active) return ["Inactive", "bg-cloud text-stone"];
    if (s.subscription_status === "active") return s.access_source === "admin_granted" ? ["Free access", "bg-sky-light text-sky-hover"] : ["Active", "bg-leaf/15 text-leaf"];
    if (s.trial_active) return ["Trial", "bg-lemon/50 text-ink"];
    if (s.subscription_status === "pending_payment") return ["Unpaid", "bg-watermelon-light text-watermelon"];
    return [s.subscription_status, "bg-cloud text-stone"];
  };

  const viewDetail = async (id) => {
    try {
      const { data } = await api.get(`/admin/students/${id}`);
      setDetail(data);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };

  const sendReport = async (s) => {
    if (!window.confirm(`Send monthly progress email to ${s.email}${s.parent_email ? ` and ${s.parent_email}` : ""}?`)) return;
    setSending(s.id);
    try {
      const { data } = await api.post(`/admin/students/${s.id}/send-monthly-report`);
      toast.success(`Report sent to ${data.sent_to.length} recipient(s)`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setSending(null); }
  };

  const previewReport = (s) => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/students/${s.id}/monthly-report-preview`;
    const token = localStorage.getItem("mw_token");
    // We need to include the token in a request; since <a target=_blank> can't set headers, do a fetch->blob->openWindow
    fetch(url, { credentials: "include", headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then((r) => r.ok ? r.text() : Promise.reject(new Error(`HTTP ${r.status}`)))
      .then((html) => {
        const blob = new Blob([html], { type: "text/html" });
        const objectUrl = URL.createObjectURL(blob);
        window.open(objectUrl, "_blank");
      })
      .catch((e) => toast.error(`Preview failed: ${e.message}`));
  };

  return (
    <AppLayout variant="admin">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-heading text-4xl sm:text-5xl font-bold text-ink">Students</h1>
            <p className="text-stone mt-1">Manage student accounts and view their progress.</p>
          </div>
          <button
            data-testid="add-student-button"
            onClick={() => { setShowAdd(true); setEditing(null); }}
            className="inline-flex items-center gap-2 h-12 px-5 rounded-full bg-watermelon text-white font-bold btn-tactile hover:bg-watermelon-hover"
          >
            <Plus className="w-5 h-5" /> New Student
          </button>
        </div>

        {(() => {
          const pending = students.filter((s) => (s.subscription_status || "active") === "pending_approval");
          if (pending.length === 0) return null;
          return (
            <div className="mb-8 bg-lemon/25 border-2 border-lemon rounded-3xl p-5" data-testid="pending-approvals">
              <div className="flex items-center gap-2 mb-3">
                <Clock3 className="w-5 h-5 text-ink" />
                <h2 className="font-heading text-xl font-bold text-ink">Pending approvals ({pending.length})</h2>
              </div>
              <ul className="space-y-2">
                {pending.map((s) => (
                  <li key={s.id} data-testid={`pending-row-${s.id}`} className="flex flex-wrap items-center justify-between gap-3 p-3 bg-white rounded-2xl">
                    <div className="min-w-0">
                      <div className="font-heading font-bold text-ink truncate">{s.name}</div>
                      <div className="text-xs text-stone truncate">{s.email}{s.parent_email ? ` · parent: ${s.parent_email}` : ""}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button data-testid={`approve-${s.id}`} onClick={() => approvePending(s)} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-leaf text-white font-bold text-sm btn-tactile hover:brightness-95">
                        <Check className="w-4 h-4" /> Approve
                      </button>
                      <button data-testid={`reject-${s.id}`} onClick={() => rejectPending(s)} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-canvas text-ink font-bold text-sm btn-tactile hover:brightness-95">
                        <X className="w-4 h-4" /> Reject
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          );
        })()}

        {showAdd && (
          <div className="mb-6">
            <StudentForm submitLabel="Create Student" onSubmit={create} onCancel={() => setShowAdd(false)} />
          </div>
        )}
        {editing && (
          <div className="mb-6">
            <StudentForm initial={editing} submitLabel="Save Changes" onSubmit={(p) => update(editing.id, p)} onCancel={() => setEditing(null)} />
          </div>
        )}

        <div className="bg-white rounded-3xl card-tactile overflow-hidden">
          {loading ? (
            <div className="p-8 text-stone">Loading…</div>
          ) : students.length === 0 ? (
            <div className="p-8 text-stone text-center">No students yet.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-canvas">
                  <tr className="text-left text-xs uppercase tracking-wider text-stone">
                    <th className="p-4 font-bold">Name</th>
                    <th className="p-4 font-bold">Email</th>
                    <th className="p-4 font-bold">WhatsApp</th>
                    <th className="p-4 font-bold">Level</th>
                    <th className="p-4 font-bold">Country</th>
                    <th className="p-4 font-bold">Status</th>
                    <th className="p-4 font-bold text-right">Actions</th>
                  </tr>
                </thead>
                <tbody data-testid="students-table" className="divide-y divide-cloud">
                  {students.map((s) => (
                    <tr key={s.id} className="hover:bg-canvas/50">
                      <td className="p-4 font-semibold text-ink">{s.name}</td>
                      <td className="p-4 text-stone">{s.email}</td>
                      <td className="p-4 text-stone" data-testid={`whatsapp-${s.id}`}>{s.whatsapp_number ? `+91 ${s.whatsapp_number}` : "—"}</td>
                      <td className="p-4"><span className="px-2 py-1 rounded-full bg-sky-light text-sky-hover text-xs font-bold">L{s.current_level ?? 1}</span></td>
                      <td className="p-4 text-stone">{s.country || "—"}</td>
                      <td className="p-4">
                        <span data-testid={`access-badge-${s.id}`} className={`px-2 py-1 rounded-full text-xs font-bold whitespace-nowrap ${accessBadge(s)[1]}`}>
                          {accessBadge(s)[0]}
                        </span>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-2 justify-end">
                          {s.subscription_status !== "active" && (
                            <button data-testid={`grant-access-${s.id}`} onClick={() => setAccess(s, true)} title="Grant free access" className="p-2 rounded-full hover:bg-leaf/15 text-leaf"><Unlock className="w-4 h-4" /></button>
                          )}
                          {s.subscription_status === "active" && s.access_source === "admin_granted" && (
                            <button data-testid={`revoke-access-${s.id}`} onClick={() => setAccess(s, false)} title="Revoke free access" className="p-2 rounded-full hover:bg-watermelon-light text-watermelon"><Lock className="w-4 h-4" /></button>
                          )}
                          <button data-testid={`preview-report-${s.id}`} onClick={() => previewReport(s)} title="Preview monthly report" className="p-2 rounded-full hover:bg-sky-light text-sky-hover"><FileText className="w-4 h-4" /></button>
                          <button data-testid={`send-report-${s.id}`} onClick={() => sendReport(s)} disabled={sending === s.id} title="Send monthly report" className="p-2 rounded-full hover:bg-lemon/30 text-ink disabled:opacity-40"><Mail className="w-4 h-4" /></button>
                          <button data-testid={`view-student-${s.id}`} onClick={() => viewDetail(s.id)} title="View" className="p-2 rounded-full hover:bg-sky-light text-sky-hover"><Eye className="w-4 h-4" /></button>
                          <button data-testid={`edit-student-${s.id}`} onClick={() => { setEditing(s); setShowAdd(false); }} title="Edit" className="p-2 rounded-full hover:bg-lemon/20 text-ink"><Pencil className="w-4 h-4" /></button>
                          <button data-testid={`toggle-active-${s.id}`} onClick={() => toggleActive(s)} title={s.is_active ? "Deactivate" : "Activate"} className="p-2 rounded-full hover:bg-watermelon-light text-watermelon">
                            {s.is_active ? <UserRoundX className="w-4 h-4" /> : <UserRoundCheck className="w-4 h-4" />}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Detail modal */}
        {detail && (
          <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setDetail(null)}>
            <div className="bg-white rounded-3xl max-w-2xl w-full max-h-[80vh] overflow-y-auto p-6 card-tactile" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="font-heading text-2xl font-bold text-ink">{detail.student.name}</h3>
                  <p className="text-stone text-sm">{detail.student.email} · Level {detail.student.current_level ?? 1}</p>
                </div>
                <button data-testid="close-student-detail" onClick={() => setDetail(null)} className="text-stone hover:text-ink px-3 py-1 rounded-full">✕</button>
              </div>
              <h4 className="font-heading text-lg font-bold text-ink mb-2">Exercise history</h4>
              {detail.attempts.length === 0 ? (
                <div className="text-stone">No attempts yet.</div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="text-left text-xs uppercase tracking-wider text-stone">
                    <tr>
                      <th className="p-2">Exercise</th>
                      <th className="p-2">Date</th>
                      <th className="p-2">Score</th>
                      <th className="p-2">Accuracy</th>
                      <th className="p-2">Time</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-cloud">
                    {detail.attempts.map((a) => (
                      <tr key={a.id}>
                        <td className="p-2 font-semibold text-ink">
                          <Link to={`/admin/attempts/${a.id}`} className="text-sky hover:underline">{a.exercise_title}</Link>
                        </td>
                        <td className="p-2 text-stone">{new Date(a.submitted_at).toLocaleString()}</td>
                        <td className="p-2 font-bold">{a.score}/{a.total_questions}</td>
                        <td className="p-2 text-leaf font-semibold">{a.accuracy}%</td>
                        <td className="p-2 text-stone">{Math.floor(a.time_taken_seconds/60)}m {a.time_taken_seconds%60}s</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}
        <DeleteAllStudentsCard onDone={load} />
      </div>
    </AppLayout>
  );
}
